import {format} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'

import {formatJobTime, parseApiDate} from '../jobs/job-schedule-format'
import type {CommitmentJobDisplay} from './commitment-job-display'
import type {TimelineJobRow} from './job-rows'
import type {UrgencyContext} from './schedule-urgency'
import {daysInViewRange} from './timeline-view'
import type {DispatchJobSchedule, ScheduleDate, ViewRange} from './types'

export type TerminalScheduleLabel = {
    /** Right-column anchor — day name, time, or horizon marker. */
    anchor: string
    /** Optional status shown before the schedule anchor. */
    status?: 'Overdue'
    /** Explicit duration when multi-day or cross-horizon. */
    duration?: string
    /** Secondary label is a neutral time range rather than an urgency hint. */
    secondaryKind?: 'time'
}

function dayAnchorLabel(day: ScheduleDate, viewRange: ViewRange): string {
    if (viewRange === 'day') {
        return day.isToday ? 'Today' : `${day.weekdayShort} ${day.dayOfMonth}`
    }
    if (day.isToday) {
        return `Today · ${day.weekdayShort} ${day.dayOfMonth}`
    }
    return `${day.weekdayShort} ${day.dayOfMonth}`
}

/** Returns whether a complete schedule ended before the current business date. */
export function scheduleEndedBeforeBusinessToday(
    schedule: DispatchJobSchedule | undefined,
): boolean {
    if (!schedule?.endDate) {
        return false
    }

    return parseApiDate(schedule.endDate) < businessCalendarDate()
}

/** Formats a persisted schedule range with the month anchored to its start. */
export function scheduledDateRangeLabel(
    job: TimelineJobRow,
    options: {includeMonthOnSingleDay?: boolean} = {},
): string | undefined {
    const startDate = job.schedule?.startDate
    if (!startDate) {
        return undefined
    }
    const endDate = job.schedule?.endDate
    const start = parseApiDate(startDate)

    if (!endDate || startDate === endDate) {
        return options.includeMonthOnSingleDay ? format(start, 'EEE MMM d') : format(start, 'EEE d')
    }

    const end = parseApiDate(endDate)
    const sameMonth =
        start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()
    return `${format(start, 'EEE MMM d')} – ${format(end, sameMonth ? 'EEE d' : 'EEE MMM d')}`
}

function timedScheduleRange(job: TimelineJobRow): string | undefined {
    const startTime = job.schedule?.startTime
    return startTime ? formatJobTime(startTime) : undefined
}

function withJobStartTimeLabel(
    job: TimelineJobRow,
    label: TerminalScheduleLabel,
): TerminalScheduleLabel {
    const time = timedScheduleRange(job)
    return time ? {...label, duration: time, secondaryKind: 'time'} : label
}

export function terminalScheduleLabel(
    job: TimelineJobRow,
    display: CommitmentJobDisplay,
    visibleDays: ScheduleDate[],
    viewRange: ViewRange,
    options: {
        viewDayOffset?: number
        isContinuationDay?: boolean
        nowHour?: number
    } = {},
): TerminalScheduleLabel {
    const viewDayOffset = options.viewDayOffset ?? job.span?.dayOffset ?? 0
    const isContinuationDay = options.isContinuationDay ?? false
    const day = visibleDays[viewDayOffset]
    const span = job.span?.daySpan ?? (job.scheduleKind === 'two_day' ? 2 : 1)

    if (job.schedule?.dateTbc) {
        return {anchor: 'Date TBC'}
    }

    if (scheduleEndedBeforeBusinessToday(job.schedule)) {
        const dateRange = scheduledDateRangeLabel(job, {includeMonthOnSingleDay: true})
        const overdueLabel: TerminalScheduleLabel = {
            anchor: dateRange ?? dayAnchorLabel(day ?? visibleDays[0]!, viewRange),
            status: 'Overdue',
        }
        return job.scheduleKind === 'two_day'
            ? overdueLabel
            : withJobStartTimeLabel(job, overdueLabel)
    }

    if (job.schedule != null && job.schedule.endDate == null) {
        return {
            anchor:
                scheduledDateRangeLabel(job, {includeMonthOnSingleDay: true}) ??
                (day ? dayAnchorLabel(day, viewRange) : '—'),
            duration: 'No end date',
        }
    }

    if (job.scheduleKind === 'timed' && display.timedLabel && job.span) {
        return {
            anchor: day ? dayAnchorLabel(day, viewRange) : display.timedLabel,
            duration: timedScheduleRange(job),
            secondaryKind: 'time',
        }
    }

    if (job.scheduleKind === 'two_day' || display.spansMultipleDays || span > 1) {
        if (isContinuationDay) {
            return {
                anchor: day ? dayAnchorLabel(day, viewRange) : 'Continues',
                duration: `Day ${viewDayOffset - (job.span?.dayOffset ?? 0) + 1} of ${span}`,
            }
        }

        return {
            anchor:
                scheduledDateRangeLabel(job) ??
                (day ? dayAnchorLabel(day, viewRange) : `${span} days`),
        }
    }

    if (!day) {
        return {anchor: '—'}
    }

    return withJobStartTimeLabel(job, {anchor: dayAnchorLabel(day, viewRange)})
}

export function terminalScheduleLabelFromContext(
    job: TimelineJobRow,
    display: CommitmentJobDisplay,
    context: UrgencyContext,
    options: {viewDayOffset?: number; isContinuationDay?: boolean} = {},
): TerminalScheduleLabel {
    return terminalScheduleLabel(job, display, context.visibleDays, context.viewRange, {
        viewDayOffset: options.viewDayOffset,
        isContinuationDay: options.isContinuationDay,
        nowHour: context.nowHour,
    })
}

export function horizonBoardTitle(visibleDays: ScheduleDate[], viewRange: ViewRange): string {
    if (visibleDays.length === 0) {
        return ''
    }
    if (viewRange === 'day') {
        const day = visibleDays[0]!
        return day.isToday ? 'Today' : day.label
    }
    const first = visibleDays[0]!
    const last = visibleDays[visibleDays.length - 1]!
    const rangeLabel =
        viewRange === '2days' ? '2-day horizon' : `${daysInViewRange(viewRange)}-day horizon`
    return `${rangeLabel} · ${first.weekdayShort} ${first.dayOfMonth} – ${last.weekdayShort} ${last.dayOfMonth}`
}

export function daySectionLabel(day: ScheduleDate, viewRange: ViewRange): string {
    if (day.isToday) {
        return viewRange === 'day' ? 'Today' : `Today · ${day.weekdayShort} ${day.dayOfMonth}`
    }
    return `${day.weekdayShort} ${day.monthLabel} ${day.dayOfMonth}`
}
